import * as React from "react";
import { type Table } from "@tanstack/react-table";
import { Plus, Eye, EyeOff } from "lucide-react";
import {
  Button,
  DataTableToolbar,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import { PermissionGate } from "@/components/security";
import type { Product } from "@/lib/types/inventory";

interface InventoryToolbarProps {
  table: Table<Product>;
  onRefresh?: () => void;
  onStartBulkAdd?: () => void;
  onSearch?: (search: string) => void;
  onFilterChange?: (filters: Record<string, string>) => void;
  userRole?: string;
  isLoading?: boolean;
}

export function InventoryToolbar({
  table,
  onStartBulkAdd,
  onSearch,
  onFilterChange,
  isLoading,
}: InventoryToolbarProps) {
  const [statusFilterValue, setStatusFilterValue] = React.useState("all");
  const [scheduleFilterValue, setScheduleFilterValue] = React.useState("all");
  const [showHidden, setShowHidden] = React.useState(false);

  const SCHEDULE_DRUG_TYPE_MAP: Record<string, string> = {
    schedule_ii: "schedule_ii",
    schedule_iii_iv: "schedule_iii,schedule_iv",
    non_controlled: "is_null",
  };

  // Track local search value for debouncing
  const [localSearch, setLocalSearch] = React.useState("");

  // Handle search changes by monitoring the column filter (for local UI state only)
  const searchFilter = table.getColumn("item_name")?.getFilterValue() as
    | string
    | undefined;

  // Sync local search with table filter (undefined means filter was cleared)
  React.useEffect(() => {
    const effectiveSearch = searchFilter ?? "";
    if (effectiveSearch !== localSearch) {
      setLocalSearch(effectiveSearch);
    }
  }, [searchFilter, localSearch]);

  // Debounce and send search to server
  React.useEffect(() => {
    const timer = setTimeout(() => {
      if (onSearch && localSearch !== undefined) {
        onSearch(localSearch);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [localSearch, onSearch]);

  return (
    <div className="flex items-center justify-between">
      <DataTableToolbar
        table={table}
        searchColumn="item_name"
        searchPlaceholder="Search inventory..."
        searchInputTestId="inventory-search-input"
      >
        <Select
          value={scheduleFilterValue}
          onValueChange={(value) => {
            setScheduleFilterValue(value);
            if (onFilterChange) {
              const filters: Record<string, string> = {};
              if (statusFilterValue !== "all")
                filters.status = statusFilterValue;
              if (value !== "all")
                filters.drug_type = SCHEDULE_DRUG_TYPE_MAP[value] ?? "";
              if (showHidden) filters.show_hidden = "true";
              onFilterChange(filters);
            }
          }}
        >
          <SelectTrigger className="h-8 w-[170px]">
            <SelectValue placeholder="All Schedules" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Schedules</SelectItem>
            <SelectItem value="schedule_ii">Schedule II</SelectItem>
            <SelectItem value="schedule_iii_iv">Schedule III-IV</SelectItem>
            <SelectItem value="non_controlled">Non-Controlled</SelectItem>
          </SelectContent>
        </Select>

        <Select
          value={statusFilterValue}
          onValueChange={(value) => {
            setStatusFilterValue(value);
            if (onFilterChange) {
              const filters: Record<string, string> = {};
              if (value !== "all") filters.status = value;
              if (scheduleFilterValue !== "all")
                filters.drug_type =
                  SCHEDULE_DRUG_TYPE_MAP[scheduleFilterValue] ?? "";
              if (showHidden) filters.show_hidden = "true";
              onFilterChange(filters);
            }
          }}
        >
          <SelectTrigger className="h-8 w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="in_stock">In Stock</SelectItem>
            <SelectItem value="reorder_needed">Re-purchase Needed</SelectItem>
            <SelectItem value="sold_out">Sold Out</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant="outline"
          size="sm"
          className={`h-8 gap-1.5 ${showHidden ? "border-secondary/50 text-secondary" : ""}`}
          onClick={() => {
            const newShowHidden = !showHidden;
            setShowHidden(newShowHidden);
            if (onFilterChange) {
              const filters: Record<string, string> = {};
              if (statusFilterValue !== "all")
                filters.status = statusFilterValue;
              if (scheduleFilterValue !== "all")
                filters.drug_type =
                  SCHEDULE_DRUG_TYPE_MAP[scheduleFilterValue] ?? "";
              if (newShowHidden) filters.show_hidden = "true";
              onFilterChange(filters);
            }
          }}
        >
          {showHidden ? (
            <Eye className="h-3.5 w-3.5 text-secondary" />
          ) : (
            <EyeOff className="h-3.5 w-3.5" />
          )}
          {showHidden ? "Showing Hidden" : "Show Hidden"}
        </Button>
      </DataTableToolbar>

      <PermissionGate permission="inventory:add_product">
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={onStartBulkAdd} disabled={isLoading}>
            <Plus className="mr-2 h-4 w-4" />
            Add Drug
          </Button>
        </div>
      </PermissionGate>
    </div>
  );
}
